import { useCallback, useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import EncoderAPI from "../api/EncoderAPI";
import { getFastStatus } from "../components/encoding/OutputStatusPanel";
import {
  AUDIO_FORMAT_KEYS,
  AUDIO_FORMAT_LIST,
  BITRATE_MARGIN,
  VIDEO_FORMAT_KEYS,
  VIDEO_FORMAT_LIST,
} from "../constants/encoding";
import { isFastFramerateMismatch } from "../utils/encodingStreams";
import {
  getComputedQuotas,
  sourceMediasToEncodingStreams,
} from "../utils/quotas";

import type { StartEncodingOutcome } from "./useMediaEncoding";
import type {
  EncodingProfile,
  PivotMedia,
  SourceMedia,
} from "../api/EncoderAPI.types";
import type { FastAsset } from "../api/MediaAPI.types";
import type { AppState } from "../reducers";
import type { ComputedQuotas } from "../utils/quotas";

interface UseEncodingPanelParams {
  sourceMedias: (SourceMedia | PivotMedia)[];
  encodingProfiles: EncodingProfile[];
  isEncoding: boolean;
  isStartingEncoding: boolean;
  onStartEncoding: (profileUuid: string) => Promise<StartEncodingOutcome>;
  fastAsset: FastAsset | null | undefined;
}

interface UseEncodingPanelResult {
  selectedProfileUuid: string | null;
  selectedProfile: EncodingProfile | null;
  setManualSelection: React.Dispatch<React.SetStateAction<string | null>>;
  resetAllQualitiesEncoded: () => void;
  allQualitiesEncoded: boolean;
  canStartEncoding: boolean;
  startEncodingTooltip: string | undefined;
  quotas: ComputedQuotas;
  videoFormatList: string[];
  audioFormatList: string[];
  isVideoFormatLocked: (formatLabel: string) => boolean;
  isAudioFormatLocked: (formatLabel: string) => boolean;
  videoFormatLockedTooltip: (formatLabel: string) => string | undefined;
  audioFormatLockedTooltip: (formatLabel: string) => string | undefined;
  isFastOptionLocked: boolean;
  isD2gOptionLocked: boolean;
  handleEncodingClick: () => void;
  isOpenEncodingWarningModal: boolean;
  closeEncodingWarningModal: () => void;
  confirmEncoding: () => void;
  isFramerateMismatch: boolean;
}

export default function useEncodingPanel({
  sourceMedias,
  encodingProfiles,
  isEncoding,
  isStartingEncoding,
  onStartEncoding,
  fastAsset,
}: UseEncodingPanelParams): UseEncodingPanelResult {
  const { t } = useTranslation("editor-content");

  const defaultProfileUuid =
    encodingProfiles.find((profile) => profile.default)?.uuid ?? null;

  const [manualSelection, setManualSelection] = useState<string | null>(null);
  const [allQualitiesEncoded, setAllQualitiesEncoded] = useState(false);
  const [isQueuedEncoding, setIsQueuedEncoding] = useState(false);
  const [isOpenEncodingWarningModal, setIsOpenEncodingWarningModal] =
    useState(false);

  const fastPlan = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options.fast_plan,
  );

  const selectedProfileUuid = manualSelection ?? defaultProfileUuid;
  const selectedProfile =
    encodingProfiles.find((p) => p.uuid === selectedProfileUuid) ?? null;
  const costFactor = selectedProfile?.cost_factor ?? 1;

  useEffect(() => {
    if (!isEncoding) setIsQueuedEncoding(false);
  }, [isEncoding]);

  const getEncodingPlanQuery = EncoderAPI.generateQuery({
    fn: EncoderAPI.getEncodingPlan,
    params: [],
  });

  const { data: encodingPlan } = useQuery(getEncodingPlanQuery);

  const encodingStreams = sourceMediasToEncodingStreams(sourceMedias);

  const quotas = getComputedQuotas(encodingStreams, encodingPlan, costFactor);
  const hasQuotaAvailable = quotas.available.seconds >= 0;

  const allStreams = sourceMedias.flatMap((src) => src.info?.streams ?? []);

  const videoStreams = allStreams.filter((s) => s.codec_type === "video");

  const sourceMaxHeight = videoStreams.reduce(
    (acc, s) => Math.max(acc, s.height ?? 0),
    0,
  );

  const sourceMaxFramerate = Math.round(
    videoStreams.reduce(
      (acc, s) => Math.max(acc, Number(s.avg_frame_rate) || 0),
      0,
    ),
  );

  const fastStatus = getFastStatus(fastAsset, fastPlan);

  const isErrorOrUnavailableFastStatus =
    fastStatus === "error" || fastStatus === "unavailable";

  const sourceMaxBitrate = allStreams
    .filter((s) => s.codec_type === "audio")
    .reduce(
      (acc, s) => Math.max(acc, s.bit_rate ? parseInt(s.bit_rate, 10) : 0),
      0,
    );

  const hasVideoOrAudioSource = allStreams.some(
    (s) => s.codec_type === "video" || s.codec_type === "audio",
  );

  const hasAudioSource = allStreams.some((s) => s.codec_type === "audio");

  const isVideoFormatLocked = useCallback(
    (formatLabel: string): boolean => {
      const requiredHeight = parseInt(formatLabel.replace("p", ""), 10);
      return sourceMaxHeight === 0 || sourceMaxHeight < requiredHeight;
    },
    [sourceMaxHeight],
  );

  const isAudioFormatLocked = useCallback(
    (formatLabel: string): boolean => {
      const requiredBitrate =
        (parseInt(formatLabel.replace("kbps", ""), 10) - BITRATE_MARGIN) * 1000;

      return sourceMaxBitrate === 0 || sourceMaxBitrate < requiredBitrate;
    },
    [sourceMaxBitrate],
  );

  const videoFormatLockedTooltip = useCallback(
    (formatLabel: string): string | undefined => {
      if (!isVideoFormatLocked(formatLabel)) return undefined;
      return sourceMaxHeight === 0 ? t("noVideoSource") : t("resolutionTooLow");
    },
    [isVideoFormatLocked, sourceMaxHeight, t],
  );

  const audioFormatLockedTooltip = useCallback(
    (formatLabel: string): string | undefined => {
      if (!isAudioFormatLocked(formatLabel)) return undefined;
      return sourceMaxBitrate === 0 ? t("noAudioSource") : t("bitrateTooLow");
    },
    [isAudioFormatLocked, sourceMaxBitrate, t],
  );

  const isFastOptionLocked =
    !hasAudioSource || sourceMaxHeight === 0 || sourceMaxHeight < 1080;

  const isD2gOptionLocked = sourceMaxHeight < 720;

  const videoFormatList = selectedProfile
    ? VIDEO_FORMAT_LIST.filter((label) => {
        const key = VIDEO_FORMAT_KEYS[label];
        return key && selectedProfile[key];
      })
    : [];

  const audioFormatList = selectedProfile
    ? AUDIO_FORMAT_LIST.filter((label) => {
        const key = AUDIO_FORMAT_KEYS[label];
        return key && selectedProfile[key];
      })
    : [];

  const hasEncodableFormat =
    videoFormatList.some((label) => !isVideoFormatLocked(label)) ||
    audioFormatList.some((label) => !isAudioFormatLocked(label)) ||
    (selectedProfile?.fast === true && !isFastOptionLocked) ||
    (selectedProfile?.ott_download_to_go === true && !isD2gOptionLocked);

  const canStartEncoding =
    !isEncoding &&
    !isStartingEncoding &&
    !isQueuedEncoding &&
    !allQualitiesEncoded &&
    selectedProfileUuid !== null &&
    sourceMedias.length > 0 &&
    hasVideoOrAudioSource &&
    hasEncodableFormat &&
    hasQuotaAvailable;

  const startEncodingTooltip = ((): string | undefined => {
    if (allQualitiesEncoded)
      return t("startEncodingTooltip.allQualitiesEncoded");
    if (isEncoding || isQueuedEncoding)
      return t("startEncodingTooltip.isEncoding");
    if (sourceMedias.length === 0) return t("startEncodingTooltip.noSource");
    if (!hasVideoOrAudioSource) return t("startEncodingTooltip.noMediaSource");
    if (!hasEncodableFormat) return t("startEncodingTooltip.noEncodableFormat");
    if (!hasQuotaAvailable) return t("startEncodingTooltip.noQuota");
    return undefined;
  })();

  const isFramerateMismatch = isFastFramerateMismatch(
    selectedProfile,
    isFastOptionLocked,
    sourceMaxFramerate,
  );

  const startEncoding = useCallback((): void => {
    if (!selectedProfileUuid) return;
    setIsQueuedEncoding(true);
    onStartEncoding(selectedProfileUuid)
      .then((response) => {
        if (response === "allQualitiesAlreadyEncoded") {
          setAllQualitiesEncoded(true);
          setIsQueuedEncoding(false);
        }
      })
      .catch(() => {
        setIsQueuedEncoding(false);
      });
  }, [selectedProfileUuid, onStartEncoding]);

  const handleEncodingClick = useCallback((): void => {
    if (!selectedProfileUuid) return;
    if (isFramerateMismatch && isErrorOrUnavailableFastStatus) {
      setIsOpenEncodingWarningModal(true);
      return;
    }
    startEncoding();
  }, [
    selectedProfileUuid,
    isFramerateMismatch,
    isErrorOrUnavailableFastStatus,
    startEncoding,
  ]);

  const confirmEncoding = useCallback((): void => {
    setIsOpenEncodingWarningModal(false);
    startEncoding();
  }, [startEncoding]);

  const closeEncodingWarningModal = useCallback(
    () => setIsOpenEncodingWarningModal(false),
    [],
  );

  const resetAllQualitiesEncoded = useCallback(
    () => setAllQualitiesEncoded(false),
    [],
  );

  return {
    selectedProfileUuid,
    selectedProfile,
    setManualSelection,
    resetAllQualitiesEncoded,
    allQualitiesEncoded,
    canStartEncoding,
    startEncodingTooltip,
    quotas,
    videoFormatList,
    audioFormatList,
    isVideoFormatLocked,
    isAudioFormatLocked,
    videoFormatLockedTooltip,
    audioFormatLockedTooltip,
    isFastOptionLocked,
    isD2gOptionLocked,
    handleEncodingClick,
    isOpenEncodingWarningModal,
    closeEncodingWarningModal,
    confirmEncoding,
    isFramerateMismatch,
  };
}
